import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Tooltip,
    Alert,
    AlertTitle,
    Chip,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Checkbox,
    Divider,
    FormControlLabel,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
} from '@mui/material';
import {
    Close as CloseIcon,
    Download as DownloadIcon,
    Edit as EditIcon,
    Visibility as VisibilityIcon,
    ExpandMore as ExpandMoreIcon,
    ArrowBack as ArrowBackIcon,
    RestartAlt as RestartAltIcon,
} from '@mui/icons-material';
import { getWorkerHTMLWithConfig, generatePDFFromHTML, CVGenerationWarnings } from '../../api/workersApi';
import { fetchWorkerWithSessionQuestions, updateSessionQuestionShowInCV, bulkUpdateSessionQuestionAnswers, BulkUpdateAnswerPayloadItem, SessionQuestionEntityFrontend, WorkerDtoFrontend, getAnswerForQuestion } from '../../api/sessionQuestionsApi';
import CVPromptConfigPanel, { PromptConfig } from './CVPromptConfigPanel';
import CVGenerationLoading from './CVGenerationLoading';
import { listPresets, savePreset, updatePreset, deletePreset, getPreset, isSystemPreset, isPresetNameTaken, canonicalizePresetPayload, CVPreset } from '../../utils/cvPresets';

type ModalStep = 'configure' | 'loading' | 'preview';

// Sections that become contenteditable when the user enters Edit Mode.
// Targeting the outer wrapper means descendants inherit contenteditable, so the
// user can freely edit titles, dates, descriptions, etc. inside each block.
const EDITABLE_SELECTORS = [
    '[data-testid="introduction-section"]',
    '[data-testid="highlights-section"]',
    '[data-testid="work-experience-section"]',
    '[data-testid="education-section"]',
    '[data-testid="languages-section"]',
    '[data-testid="profile-strengths"]',
    '[data-testid="profile-candidate-details"]',
    '[data-testid="contact-footer"]',
    '[data-testid^="questionnaire-page-"]',
    '[data-testid="marketing-content"]',
];

const EDIT_MODE_STYLE_ID = 'cv-edit-mode-style';
const EDIT_MODE_STYLES = `
[contenteditable="true"] {
    outline: 1px dashed transparent;
    outline-offset: 2px;
    transition: outline-color 0.15s ease, background-color 0.15s ease;
    cursor: text;
}
[contenteditable="true"]:hover {
    outline-color: var(--cv-primary, #4caf50);
    background-color: rgba(76, 175, 80, 0.06);
}
[contenteditable="true"]:focus {
    outline: 2px solid var(--cv-primary, #4caf50);
    background-color: rgba(76, 175, 80, 0.10);
}
`;

interface CVEditorModalProps {
    open: boolean;
    onClose: () => void;
    workerId: string;
    workerName?: string;
    includePersonalInfo?: boolean;
    template?: 'classic' | 'modern';
    /** Called after edits are persisted so the parent (e.g. WorkerDetail) can re-fetch the worker. */
    onWorkerDataChanged?: () => void;
}

interface EditableItem {
    id: string;
    title: string;
    visible: boolean;
    element: HTMLElement;
    parentSection?: string;
}

interface EditableSection {
    id: string;
    title: string;
    visible: boolean;
    items: EditableItem[];
    element: HTMLElement;
}

const CVEditorModal: React.FC<CVEditorModalProps> = ({
    open,
    onClose,
    workerId,
    workerName = 'Worker',
    includePersonalInfo = true,
    onWorkerDataChanged,
}) => {
    const [step, setStep] = useState<ModalStep>('configure');
    const [htmlContent, setHtmlContent] = useState<string>('');
    // Snapshot of the original AI-generated HTML so the user can revert in-preview edits.
    const [originalHtml, setOriginalHtml] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editableSections, setEditableSections] = useState<EditableSection[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [generatingPDF, setGeneratingPDF] = useState(false);
    const [aiWarnings, setAiWarnings] = useState<CVGenerationWarnings | null>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // Questionnaire items state
    const [sessionQuestions, setSessionQuestions] = useState<SessionQuestionEntityFrontend[]>([]);
    const [worker, setWorker] = useState<WorkerDtoFrontend | null>(null);
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const [updatingQuestionId, setUpdatingQuestionId] = useState<number | null>(null);
    const [savingEdits, setSavingEdits] = useState(false);

    // Preserve config when going back from preview
    const [lastConfig, setLastConfig] = useState<PromptConfig | null>(null);

    // Master toggle: include the questionnaire page (page 2) in CV output.
    // Source of truth lives in PromptConfig (set in the Configure step). This state mirrors
    // it for the preview-step master checkbox so toggling there round-trips back to the config.
    const [includeQuestionnairePage, setIncludeQuestionnairePageState] = useState<boolean>(true);
    const setIncludeQuestionnairePage = (next: boolean) => {
        setIncludeQuestionnairePageState(next);
        setLastConfig(prev => (prev ? { ...prev, includeQuestionnairePage: next } : prev));
    };

    // Preset management
    const [presets, setPresets] = useState<CVPreset[]>(() => listPresets());
    const [selectedPresetId, setSelectedPresetId] = useState<string>('');
    const [savePresetDialogOpen, setSavePresetDialogOpen] = useState(false);
    const [savePresetName, setSavePresetName] = useState('');
    const [savePresetError, setSavePresetError] = useState<string | null>(null);

    // Load worker + session questions when modal opens.
    // Answer values live on the worker entity post-PR1; questions hold metadata only.
    const loadSessionQuestions = async () => {
        setLoadingQuestions(true);
        try {
            const wrapped = await fetchWorkerWithSessionQuestions(workerId);
            if (wrapped) {
                setWorker(wrapped.worker);
                // "Answered" is determined by answeredAt (the session_question metadata)
                // rather than a non-empty answer value, since answers project from worker.
                const answeredQuestions = wrapped.questions.filter(q => q.answeredAt != null);
                setSessionQuestions(answeredQuestions);
            } else {
                setWorker(null);
                setSessionQuestions([]);
            }
        } catch (err) {
            console.error('Error loading session questions:', err);
        } finally {
            setLoadingQuestions(false);
        }
    };

    // Generate CV with config (called from CVPromptConfigPanel)
    const handleGenerate = async (config: PromptConfig) => {
        setLastConfig(config);
        setStep('loading');
        setError(null);
        setAiWarnings(null);
        setLoading(true);
        try {
            const excludedQuestionIds = Object.entries(config.questionToggles)
                .filter(([, enabled]) => !enabled)
                .map(([id]) => Number(id));
            const hasContactInfo = config.contactInfo?.name || config.contactInfo?.email || config.contactInfo?.phone;
            const result = await getWorkerHTMLWithConfig(workerId, {
                includePersonalInfo,
                extraInstructions: config.extraInstructions || undefined,
                categoryToggles: config.categoryToggles,
                excludedQuestionIds: excludedQuestionIds.length > 0 ? excludedQuestionIds : undefined,
                language: config.language || 'NL',
                contactInfo: hasContactInfo ? config.contactInfo : undefined,
                theme: config.theme || 'modern-green',
            });
            if (result) {
                setHtmlContent(result.html);
                setOriginalHtml(result.html);
                if (result.warnings) {
                    setAiWarnings(result.warnings);
                }
                setStep('preview');
            } else {
                setError('Failed to generate CV content');
                setStep('configure');
            }
        } catch (err) {
            setError('Error generating CV content');
            console.error('Error generating CV:', err);
            setStep('configure');
        } finally {
            setLoading(false);
        }
    };

    // Persist plain-text answer edits from the Configure step back to the worker's session questions.
    // Only entries the panel deemed "safe" (TEXT/TEXT_AREA/NUMBER/DATE) are passed in here.
    const handleSaveEditsToWorker = async (updates: BulkUpdateAnswerPayloadItem[]) => {
        if (updates.length === 0) return;
        setSavingEdits(true);
        setError(null);
        try {
            await bulkUpdateSessionQuestionAnswers(workerId, updates);
            // Refresh originals so the panel's diff resets, and clear stored overrides for the saved IDs.
            await loadSessionQuestions();
            const savedIds = new Set(updates.map(u => u.sessionQuestionId));
            setLastConfig(prev => prev ? {
                ...prev,
                questionOverrides: prev.questionOverrides.filter(ov => !savedIds.has(ov.id)),
            } : prev);
            // Notify the parent (WorkerDetail) so it can re-fetch the worker — personal-info
            // edits are mirrored onto the WorkerEntity by the backend, so the worker form/list
            // would otherwise stay stale until the page reloads.
            onWorkerDataChanged?.();
        } catch (err) {
            console.error('Error saving edits to worker:', err);
            setError('Failed to save edits to worker. Please try again.');
        } finally {
            setSavingEdits(false);
        }
    };

    // Toggle showInCV for a session question
    const handleToggleShowInCV = async (questionId: number, currentValue: boolean) => {
        setUpdatingQuestionId(questionId);
        try {
            const newValue = !currentValue;
            await updateSessionQuestionShowInCV(questionId, newValue);
            setSessionQuestions(prev =>
                prev.map(q => q.id === questionId ? { ...q, showInCV: newValue } : q)
            );
            // Re-generate with last config to reflect the change
            if (lastConfig) {
                await handleGenerate(lastConfig);
            }
        } catch (err) {
            console.error('Error updating showInCV:', err);
            setError('Failed to update question visibility');
        } finally {
            setUpdatingQuestionId(null);
        }
    };

    // --- Iframe DOM is the source of truth for the *rendered* preview, including in-place edits.
    // We bind iframe.srcDoc to htmlContent (the AI-generated baseline) and apply visibility +
    // edit-mode by directly mutating the iframe DOM after load. This way, contenteditable text
    // changes survive section/item visibility toggles (no srcDoc reload → no cursor jumps).

    const applyVisibilityToIframe = useCallback(() => {
        const doc = iframeRef.current?.contentDocument;
        if (!doc) return;

        editableSections.forEach(section => {
            const sectionEl = doc.querySelector(`[data-testid="${section.id}"]`);
            if (sectionEl) {
                (sectionEl as HTMLElement).style.display = section.visible ? '' : 'none';
            }
            section.items.forEach(item => {
                const itemEl = doc.querySelector(`[data-testid="${item.id}"]`);
                if (itemEl) {
                    (itemEl as HTMLElement).style.display = item.visible ? '' : 'none';
                }
            });
        });

        // Master questionnaire-page toggle: hide all questionnaire pages.
        doc.querySelectorAll('[data-testid^="questionnaire-page-"]').forEach(el => {
            (el as HTMLElement).style.display = includeQuestionnairePage ? '' : 'none';
        });
    }, [editableSections, includeQuestionnairePage]);

    const applyEditModeToIframe = useCallback(() => {
        const doc = iframeRef.current?.contentDocument;
        if (!doc) return;

        if (isEditing) {
            EDITABLE_SELECTORS.forEach(sel => {
                doc.querySelectorAll(sel).forEach(el => {
                    (el as HTMLElement).setAttribute('contenteditable', 'true');
                    (el as HTMLElement).setAttribute('spellcheck', 'true');
                });
            });
            if (!doc.getElementById(EDIT_MODE_STYLE_ID) && doc.head) {
                const style = doc.createElement('style');
                style.id = EDIT_MODE_STYLE_ID;
                style.textContent = EDIT_MODE_STYLES;
                doc.head.appendChild(style);
            }
        } else {
            doc.querySelectorAll('[contenteditable]').forEach(el => {
                el.removeAttribute('contenteditable');
                el.removeAttribute('spellcheck');
            });
            doc.getElementById(EDIT_MODE_STYLE_ID)?.remove();
        }
    }, [isEditing]);

    const handleIframeLoad = () => {
        applyVisibilityToIframe();
        applyEditModeToIframe();
    };

    // --- Extract sections/items from htmlContent, not iframe ---
    const extractEditableSections = () => {
        if (!htmlContent) return;
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, 'text/html');
        const sections: EditableSection[] = [];

        const sectionConfig = [
            {
                testId: 'work-experience-section',
                itemTestIdPattern: 'work-experience-',
                label: 'Work Experience',
                titleExtractor: (element: Element) => {
                    const titleEl = element.querySelector('.content-item-title');
                    const dateEl = element.querySelector('.content-item-date');
                    return titleEl?.textContent || dateEl?.textContent || 'Work Experience';
                }
            },
            {
                testId: 'education-section',
                itemTestIdPattern: 'certification-',
                label: 'Education',
                titleExtractor: (element: Element) => {
                    const titleEl = element.querySelector('.content-item-title');
                    const dateEl = element.querySelector('.content-item-date');
                    return titleEl?.textContent || dateEl?.textContent || 'Education';
                }
            },
            {
                testId: 'languages-section',
                itemTestIdPattern: 'language-',
                label: 'Languages',
                titleExtractor: (element: Element) => {
                    const nameEl = element.querySelector('.language-name');
                    return nameEl?.textContent || 'Language';
                }
            },
            {
                testId: 'highlights-section',
                itemTestIdPattern: 'highlight-tag-',
                label: 'Highlights',
                titleExtractor: (element: Element) => {
                    return element.textContent?.trim() || 'Highlight';
                }
            },
            {
                testId: 'introduction-section',
                itemTestIdPattern: 'introduction-',
                label: 'Introduction',
                titleExtractor: (element: Element) => {
                    const testId = element.getAttribute('data-testid');
                    if (testId === 'introduction-header') {
                        return 'Introduction Header';
                    } else if (testId === 'introduction-content') {
                        return 'Introduction Content';
                    }
                    return 'Introduction Text';
                }
            },
            {
                testId: 'profile-section',
                itemTestIdPattern: 'profile-',
                label: 'Profile',
                titleExtractor: (element: Element) => {
                    const testId = element.getAttribute('data-testid');
                    if (testId === 'profile-picture-container') {
                        return 'Profile Picture';
                    } else if (testId === 'profile-strengths') {
                        return 'Profile Strengths';
                    } else if (testId === 'profile-candidate-details') {
                        return 'Profile Details';
                    }
                    return 'Profile Information';
                }
            }
        ];

        sectionConfig.forEach(config => {
            const sectionElement = doc.querySelector(`[data-testid="${config.testId}"]`);
            if (sectionElement) {
                const sectionId = config.testId;
                const sectionTitle = config.label;
                const items: EditableItem[] = [];

                if (config.testId === 'profile-section') {
                    const candidateDetails = sectionElement.querySelector('[data-testid="profile-candidate-details"]');
                    const profilePicture = sectionElement.querySelector('[data-testid="profile-picture-container"]');
                    const strengths = sectionElement.querySelector('[data-testid="profile-strengths"]');

                    if (candidateDetails) {
                        items.push({
                            id: 'profile-candidate-details',
                            title: 'Profile Details',
                            visible: true,
                            element: candidateDetails as HTMLElement,
                            parentSection: sectionId
                        });
                    }

                    if (profilePicture) {
                        items.push({
                            id: 'profile-picture-container',
                            title: 'Profile Picture',
                            visible: true,
                            element: profilePicture as HTMLElement,
                            parentSection: sectionId
                        });
                    }

                    if (strengths) {
                        items.push({
                            id: 'profile-strengths',
                            title: 'Profile Strengths',
                            visible: true,
                            element: strengths as HTMLElement,
                            parentSection: sectionId
                        });
                    }
                } else {
                    const itemElements = Array.from(sectionElement.querySelectorAll(`[data-testid^="${config.itemTestIdPattern}"]`));
                    itemElements.forEach((itemElement, itemIndex) => {
                        const testId = itemElement.getAttribute('data-testid');
                        const itemId = testId || `${sectionId}-item-${itemIndex}`;
                        const itemTitle = config.titleExtractor(itemElement);
                        items.push({
                            id: itemId,
                            title: itemTitle,
                            visible: true,
                            element: itemElement as HTMLElement,
                            parentSection: sectionId
                        });
                    });
                }

                sections.push({
                    id: sectionId,
                    title: sectionTitle,
                    visible: true,
                    items,
                    element: sectionElement as HTMLElement
                });
            }
        });

        setEditableSections(prevSections => {
            return sections.map(section => {
                const prevSection = prevSections.find(s => s.id === section.id);
                return {
                    ...section,
                    visible: prevSection ? prevSection.visible : true,
                    items: section.items.map(item => {
                        const prevItem = prevSection?.items.find(i => i.id === item.id);
                        return {
                            ...item,
                            visible: prevItem ? prevItem.visible : true,
                        };
                    }),
                };
            });
        });
    };

    // Load session questions when modal opens
    useEffect(() => {
        if (open && workerId) {
            loadSessionQuestions();
            setStep('configure');
            setHtmlContent('');
            setOriginalHtml('');
            setError(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, workerId]);

    // When the AI generates new HTML (or user resets), re-extract editable sections.
    // Iframe srcDoc is bound to htmlContent declaratively, so the iframe reloads automatically.
    useEffect(() => {
        if (htmlContent) {
            extractEditableSections();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [htmlContent]);

    // Visibility / questionnaire-page changes — mutate iframe DOM in place.
    useEffect(() => {
        if (htmlContent) applyVisibilityToIframe();
    }, [editableSections, includeQuestionnairePage, htmlContent, applyVisibilityToIframe]);

    // Edit-mode toggle — mutate iframe DOM in place (no reload, edits preserved).
    useEffect(() => {
        if (htmlContent) applyEditModeToIframe();
    }, [isEditing, htmlContent, applyEditModeToIframe]);

    const toggleSectionVisibility = (sectionId: string) => {
        setEditableSections(prev =>
            prev.map(section => {
                if (section.id === sectionId) {
                    const newVisible = !section.visible;
                    return {
                        ...section,
                        visible: newVisible,
                        items: section.items.map(item => ({ ...item, visible: newVisible }))
                    };
                }
                return section;
            })
        );
    };

    const toggleItemVisibility = (itemId: string) => {
        setEditableSections(prev =>
            prev.map(section => ({
                ...section,
                items: section.items.map(item => {
                    if (item.id === itemId) {
                        const newVisible = !item.visible;
                        return { ...item, visible: newVisible };
                    }
                    return item;
                })
            }))
        );
    };

    // Build a preset payload from current state (used for save + dirty detection)
    const buildCurrentPresetPayload = () => {
        const hiddenSectionIds = editableSections.filter(s => !s.visible).map(s => s.id);
        const hiddenItemIds = editableSections.flatMap(s => s.items.filter(i => !i.visible).map(i => i.id));
        return {
            config: lastConfig,
            sectionOrder: [] as string[],
            hiddenSectionIds,
            hiddenItemIds,
            includeQuestionnairePage,
        };
    };

    // Compare current state against the selected preset to detect unsaved changes.
    // Uses canonicalize to handle no-op edits (toggle on→off→on, key reordering, etc.)
    const isDirty = (() => {
        if (!selectedPresetId || !lastConfig) return false;
        const preset = getPreset(selectedPresetId);
        if (!preset) return false;
        const currentCanon = canonicalizePresetPayload({
            ...buildCurrentPresetPayload(),
            config: lastConfig,
        });
        const presetCanon = canonicalizePresetPayload({
            config: preset.config,
            sectionOrder: preset.sectionOrder,
            hiddenSectionIds: preset.hiddenSectionIds,
            hiddenItemIds: preset.hiddenItemIds,
            includeQuestionnairePage: preset.includeQuestionnairePage,
        });
        return currentCanon !== presetCanon;
    })();

    const handleLoadPreset = (presetId: string) => {
        setSelectedPresetId(presetId);
        if (!presetId) return;
        const preset = getPreset(presetId);
        if (!preset) return;

        // Apply configure-step settings via lastConfig (CVPromptConfigPanel reads from initialConfig)
        setLastConfig(preset.config);

        // Apply preview-step settings
        setIncludeQuestionnairePage(preset.includeQuestionnairePage);

        // Apply hidden section/item visibility (only meaningful once we have htmlContent extracted)
        if (preset.hiddenSectionIds.length > 0 || preset.hiddenItemIds.length > 0) {
            setEditableSections(prev => prev.map(s => ({
                ...s,
                visible: !preset.hiddenSectionIds.includes(s.id),
                items: s.items.map(it => ({
                    ...it,
                    visible: !preset.hiddenItemIds.includes(it.id),
                })),
            })));
        }
    };

    const handleSavePreset = () => {
        const name = savePresetName.trim();
        if (!name || !lastConfig) return;
        if (isPresetNameTaken(name)) {
            setSavePresetError(`A preset named "${name}" already exists. Pick another name.`);
            return;
        }
        const payload = buildCurrentPresetPayload();
        const newPreset = savePreset({ ...payload, config: lastConfig, name });
        setPresets(listPresets());
        setSelectedPresetId(newPreset.id);
        setSavePresetDialogOpen(false);
        setSavePresetName('');
        setSavePresetError(null);
    };

    const handleUpdatePreset = () => {
        if (!selectedPresetId || selectedPresetId.startsWith('system:') || !lastConfig) return;
        const payload = buildCurrentPresetPayload();
        updatePreset(selectedPresetId, { ...payload, config: lastConfig });
        setPresets(listPresets());
    };

    const handleDeletePreset = () => {
        if (!selectedPresetId || selectedPresetId.startsWith('system:')) return;
        deletePreset(selectedPresetId);
        setPresets(listPresets());
        setSelectedPresetId('');
    };

    const selectedPresetIsUser = selectedPresetId && !selectedPresetId.startsWith('system:');

    /** Serialize the live iframe DOM (with user edits + visibility), strip edit-mode markers, return clean HTML. */
    const getIframeHTMLForExport = (): string | null => {
        const doc = iframeRef.current?.contentDocument;
        if (!doc) return null;
        // Round-trip via DOMParser so we don't mutate the live iframe (would yank cursor / outlines).
        const serialized = '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
        const clone = new DOMParser().parseFromString(serialized, 'text/html');
        clone.querySelectorAll('[contenteditable]').forEach(el => {
            el.removeAttribute('contenteditable');
            el.removeAttribute('spellcheck');
        });
        clone.getElementById(EDIT_MODE_STYLE_ID)?.remove();
        return '<!DOCTYPE html>\n' + clone.documentElement.outerHTML;
    };

    const handleDownloadPDF = async () => {
        setGeneratingPDF(true);
        try {
            const modifiedHTML = getIframeHTMLForExport();
            if (!modifiedHTML) {
                setError('No CV content available');
                return;
            }
            await generatePDFFromHTML(workerId, modifiedHTML);
        } catch (err) {
            setError('Error generating PDF');
            console.error('Error generating PDF:', err);
        } finally {
            setGeneratingPDF(false);
        }
    };

    const handleResetEdits = () => {
        if (!originalHtml) return;
        // Force the iframe to reload from the original AI-generated HTML.
        // Setting htmlContent to '' first ensures React re-fires the srcDoc change even when content is identical.
        setHtmlContent('');
        setTimeout(() => setHtmlContent(originalHtml), 0);
    };

    const handleBackToConfig = () => {
        setStep('configure');
    };

    // --- Title per step ---
    const getTitle = () => {
        switch (step) {
            case 'configure': return `CV Editor - ${workerName} - Configure`;
            case 'loading': return `CV Editor - ${workerName} - Generating...`;
            case 'preview': return `CV Editor - ${workerName} - Preview`;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: {
                    height: '90vh',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                }
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    {getTitle()}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {step === 'preview' && (
                        <Tooltip title={isEditing ? "Preview Mode" : "Edit Mode"}>
                            <IconButton onClick={() => setIsEditing(!isEditing)}>
                                {isEditing ? <VisibilityIcon /> : <EditIcon />}
                            </IconButton>
                        </Tooltip>
                    )}
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, minHeight: 0 }}>
                {error && (
                    <Alert severity="error" sx={{ m: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Step 1: Configure AI Input */}
                {step === 'configure' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                        {/* Preset Toolbar */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                <InputLabel>CV Preset</InputLabel>
                                <Select
                                    value={selectedPresetId}
                                    label="CV Preset"
                                    onChange={(e) => handleLoadPreset(e.target.value)}
                                >
                                    <MenuItem value=""><em>None (custom)</em></MenuItem>
                                    {presets.map(p => (
                                        <MenuItem key={p.id} value={p.id}>
                                            {isSystemPreset(p) ? `★ ${p.name}` : p.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {selectedPresetIsUser && isDirty && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    onClick={handleUpdatePreset}
                                >
                                    Save Changes
                                </Button>
                            )}
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => { setSavePresetError(null); setSavePresetDialogOpen(true); }}
                                disabled={!lastConfig}
                            >
                                Save as New Preset…
                            </Button>
                            {selectedPresetIsUser && (
                                <Button size="small" color="error" onClick={handleDeletePreset}>
                                    Delete
                                </Button>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                {selectedPresetIsUser && isDirty
                                    ? 'Unsaved changes — click "Save Changes" to update this preset'
                                    : 'Presets save your config + theme + visibility'}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                            <CVPromptConfigPanel
                                sessionQuestions={sessionQuestions}
                                worker={worker}
                                onGenerate={handleGenerate}
                                loading={loadingQuestions}
                                initialConfig={lastConfig}
                                onConfigChange={(cfg) => {
                                    setLastConfig(cfg);
                                    setIncludeQuestionnairePageState(cfg.includeQuestionnairePage);
                                }}
                                onSaveEdits={handleSaveEditsToWorker}
                                savingEdits={savingEdits}
                            />
                        </Box>
                    </Box>
                )}

                {/* Save Preset Dialog */}
                <Dialog open={savePresetDialogOpen} onClose={() => { setSavePresetDialogOpen(false); setSavePresetError(null); }} maxWidth="xs" fullWidth>
                    <DialogTitle>Save CV Preset</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Preset name"
                            value={savePresetName}
                            onChange={(e) => { setSavePresetName(e.target.value); setSavePresetError(null); }}
                            sx={{ mt: 1 }}
                            error={!!savePresetError}
                            helperText={savePresetError || ' '}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSavePreset(); }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => { setSavePresetDialogOpen(false); setSavePresetError(null); }}>Cancel</Button>
                        <Button variant="contained" onClick={handleSavePreset} disabled={!savePresetName.trim()}>Save</Button>
                    </DialogActions>
                </Dialog>

                {/* Step 2: Loading */}
                {step === 'loading' && (
                    <CVGenerationLoading />
                )}

                {/* AI Fallback Warning */}
                {step === 'preview' && aiWarnings && (aiWarnings.storyFallback || aiWarnings.skillsFallback || aiWarnings.highlightsFallback) && (
                    <Alert severity="warning" sx={{ m: 2 }}>
                        <AlertTitle sx={{ fontWeight: 600 }}>Some AI content used fallback templates</AlertTitle>
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5, mb: 0.5 }}>
                            {aiWarnings.storyFallback && <Chip size="small" label="Story" color="warning" variant="outlined" />}
                            {aiWarnings.skillsFallback && <Chip size="small" label="Skills" color="warning" variant="outlined" />}
                            {aiWarnings.highlightsFallback && <Chip size="small" label="Highlights" color="warning" variant="outlined" />}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            These sections were generated with templates instead of AI. Try regenerating for better results.
                        </Typography>
                    </Alert>
                )}

                {/* Step 3: Preview + Section Toggles */}
                {step === 'preview' && (
                    <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                        {/* Left Panel - Section Controls */}
                        {isEditing && (
                            <Box sx={{
                                width: 350,
                                borderRight: 1,
                                borderColor: 'divider',
                                p: 2,
                                overflowY: 'auto'
                            }}>
                                <Typography variant="h6" gutterBottom>
                                    CV Sections
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Toggle sections and individual items to include/exclude from the final CV. Click any text in the preview to edit it directly.
                                </Typography>
                                <Box sx={{ mb: 2 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<RestartAltIcon />}
                                        onClick={handleResetEdits}
                                        disabled={!originalHtml}
                                    >
                                        Reset text edits
                                    </Button>
                                </Box>

                                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={includeQuestionnairePage}
                                                onChange={(e) => setIncludeQuestionnairePage(e.target.checked)}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                Include Questionnaire Page (page 2)
                                            </Typography>
                                        }
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                                        Hides all questionnaire pages from the CV output regardless of individual question settings.
                                    </Typography>
                                </Box>

                                {editableSections.map((section) => (
                                    <Accordion key={section.id} sx={{ mb: 1 }}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            onClick={(e) => {
                                                if ((e.target as HTMLElement).closest('.cv-section-checkbox')) {
                                                    e.stopPropagation();
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                <Checkbox
                                                    className="cv-section-checkbox"
                                                    checked={section.visible}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        toggleSectionVisibility(section.id);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    sx={{ mr: 1 }}
                                                />
                                                <Typography variant="subtitle1" sx={{ userSelect: 'none' }}>
                                                    {section.title}
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <List dense>
                                                {section.items.map((item) => (
                                                    <ListItem key={item.id} sx={{ py: 0 }}>
                                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                                            <Checkbox
                                                                size="small"
                                                                checked={item.visible}
                                                                onChange={() => toggleItemVisibility(item.id)}
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={item.title}
                                                            primaryTypographyProps={{
                                                                variant: 'body2',
                                                                sx: {
                                                                    textDecoration: item.visible ? 'none' : 'line-through',
                                                                    color: item.visible ? 'text.primary' : 'text.disabled'
                                                                }
                                                            }}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}

                                {/* Questionnaire Items Section */}
                                {sessionQuestions.length > 0 && includeQuestionnairePage && (
                                    <Box sx={{ mt: 3 }}>
                                        <Divider sx={{ mb: 2 }} />
                                        <Typography variant="h6" gutterBottom>
                                            Questionnaire Items
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            Toggle which questionnaire answers appear in the CV
                                        </Typography>

                                        {loadingQuestions ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                                <CircularProgress size={24} />
                                            </Box>
                                        ) : (
                                            <List dense>
                                                {sessionQuestions.map((question) => (
                                                    <ListItem
                                                        key={question.id}
                                                        sx={{
                                                            py: 0.5,
                                                            opacity: question.showInCV !== false ? 1 : 0.6
                                                        }}
                                                    >
                                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                                            <Checkbox
                                                                edge="start"
                                                                checked={question.showInCV !== false}
                                                                onChange={() => handleToggleShowInCV(question.id, question.showInCV !== false)}
                                                                disabled={updatingQuestionId === question.id}
                                                                size="small"
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={question.questionShorten || question.question || 'Question'}
                                                            secondary={(() => {
                                                                const ans = getAnswerForQuestion(question, worker);
                                                                if (typeof ans === 'string') {
                                                                    return ans.substring(0, 50) + (ans.length > 50 ? '...' : '');
                                                                }
                                                                if (Array.isArray(ans)) {
                                                                    return `${ans.length} items`;
                                                                }
                                                                return '';
                                                            })()}
                                                            primaryTypographyProps={{
                                                                variant: 'body2',
                                                                sx: {
                                                                    fontWeight: 500,
                                                                    textDecoration: question.showInCV !== false ? 'none' : 'line-through'
                                                                }
                                                            }}
                                                            secondaryTypographyProps={{ variant: 'caption' }}
                                                        />
                                                        {updatingQuestionId === question.id && (
                                                            <CircularProgress size={16} sx={{ ml: 1 }} />
                                                        )}
                                                    </ListItem>
                                                ))}
                                            </List>
                                        )}
                                    </Box>
                                )}

                                {editableSections.length === 0 && sessionQuestions.length === 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                        No editable sections found
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {/* Right Panel - CV Preview */}
                        <Box sx={{ flex: 1, position: 'relative', minHeight: 0, overflow: 'auto' }}>
                            {loading ? (
                                <CVGenerationLoading />
                            ) : (
                                <iframe
                                    ref={iframeRef}
                                    srcDoc={htmlContent}
                                    scrolling="auto"
                                    onLoad={handleIframeLoad}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        border: 'none',
                                        backgroundColor: 'white'
                                    }}
                                    title="CV Preview"
                                />
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                {step === 'configure' && (
                    <Button onClick={onClose}>
                        Cancel
                    </Button>
                )}

                {step === 'loading' && (
                    <Button onClick={onClose} disabled>
                        Cancel
                    </Button>
                )}

                {step === 'preview' && (
                    <>
                        <Button
                            onClick={handleBackToConfig}
                            startIcon={<ArrowBackIcon />}
                        >
                            Back to Configuration
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        <Button
                            variant="contained"
                            onClick={handleDownloadPDF}
                            disabled={generatingPDF || loading}
                            startIcon={generatingPDF ? <CircularProgress size={16} /> : <DownloadIcon />}
                        >
                            {generatingPDF ? 'Generating PDF...' : 'Download PDF'}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CVEditorModal;
